import React, { useCallback, useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import {
  Alert,
  Box,
  Grid,
  Link as MuiLink,
  Paper,
  Toolbar,
  Typography,
} from '@mui/material';
import { useParams } from 'react-router-dom';

import ContentBox from '../../components/layout/ContentBox';
import DLFrame from '../../components/digitalTrial/DLFrame';
import CaseList from '../../components/digitalTrial/CaseList';
import DigitalSectionToolbar from '../../components/digitalTrial/DigitalSectionToolbar';
import {
  DigitalQuestion,
  RootState,
} from '../../redux/types';
import {
  fetchFullDigitalSurvey,
  setSelectedDigitalsection,
  updateDigitalSection,
  setSelectedCase,
  addDigitalCase,
  setDigitalCases,
  updateDigitalCase,
  deleteDigitalCase,
  addDigitalQuestion,
  updateDigitalQuestion,
  deleteDigitalQuestion,
  fetchDigitalGroundTruths,
  addOrUpdateDigitalGroundTruth,
  deleteDigitalGroundTruth,
  showNotification,
  showErrorNotification,
  setDigitalQuestions,
} from '../../redux/actions';
import {
  selectDigitalSections,
  selectDigitalCases,
  selectNormalQuestionsDRT,
  selectSelectedSection,
  selectSelectedCase,
  selectContextQuestionsDRT,
  selectDigitalGTDictionaryByQuestionAndCaseDRT,
} from '../../redux/reducers/SurveyDataReducer';
import DigitalGroundTruthEditor from '../../components/digitalTrial/DigitalGroundTruthEditor';
import SurveySelector from '../../components/digitalTrial/SurveySelector';
import DigitalTimerSetter from '../../components/digitalTrial/DigitalTimerSetter';
import DigitalScoreEditor from '../../components/digitalTrial/DigitalScoreEditor';
import HeadingContainerDRT from '../../components/digitalTrial/HeadingContainerDRT';
import DigitalContextQuestionEditor from '../../components/digitalTrial/DigitalContextQuestionEditor';
import QuestionEditor from '../../components/questions/QuestionEditor';
import ApiClient from '../../loopback/apiClient';
import ColorContext from '../../context/ColorContext';
import { selectIsEditorPrivileged } from '../../redux/reducers/Users';

type Props = PropsFromRedux;
type MatchParams = {
  trialId: string;
};

const DigitalSurveyEditor: React.FC<Props> = ({
  digitalSurvey,
  digitalSections,
  selectedSection,
  selectedCase,
  sectionIds,
  digitalCases,
  digitalQuestions,
  nonEvaluableDigitalQuestions,
  DGTDictionaryByQuestionAndCase,
  isEditorPrivileged,
  fetchFullDigitalSurvey: fetchFullDigitalSurveyAction,
  setSelectedDigitalsection: setSelectedDigitalsectionAction,
  updateDigitalSection: updateDigitalSectionAction,
  setSelectedCase: setSelectedCaseAction,
  addDigitalCase: addDigitalCaseAction,
  setDigitalCases: setDigitalCasesAction,
  updateDigitalCase: updateDigitalCaseAction,
  deleteDigitalCase: deleteDigitalCaseAction,
  addDigitalQuestion: addDigitalQuestionAction,
  updateDigitalQuestion: updateDigitalQuestionAction,
  deleteDigitalQuestion: deleteDigitalQuestionAction,
  setDigitalQuestions: setDigitalQuestionsAction,
  fetchDigitalGroundTruths: fetchDigitalGroundTruthsAction,
  addOrUpdateDigitalGroundTruth: addOrUpdateDigitalGroundTruthAction,
  deleteDigitalGroundTruth: deleteDigitalGroundTruthAction,
  showNotification: showNotificationAction,
  showErrorNotification: showErrorNotificationAction,
}: PropsFromRedux) => {
  const params = useParams<MatchParams>();
  const [submissionExist, setSubmisionExist] = useState<boolean>(false);
  const digitalTrialId = params.trialId;
  const saveQuestionSortOrder = useCallback(async (dq: DigitalQuestion[]) => {
    const promiseArr = dq.map((q) => ApiClient.updateDigitalQuestion(q));
    try {
      const resolvedList = await Promise.all(promiseArr);
      setDigitalQuestionsAction(resolvedList);
    } catch (e) {
      showErrorNotificationAction(JSON.stringify(e));
    }
  }, [setDigitalQuestionsAction, showErrorNotificationAction]);

  useEffect(() => {
    if (sectionIds.length === 0) {
      setSubmisionExist(false);
      return;
    }

    const allSubmissionMetas = sectionIds.map(
      (id) => ApiClient.getDigitalSubmissionMetaBySectionId({ selectedSectionId: id }),
    );

    Promise.all(allSubmissionMetas).then((res) => {
      const flat = res.flat();
      setSubmisionExist(flat.length > 0);
    });
  }, [sectionIds]);
  return (
    <ColorContext.Provider value="tertiary">
      <ContentBox title="Digital Survey Editor">
        <Toolbar disableGutters>
          <Box flexGrow="1" display="flex">
            <SurveySelector
              trialId={Number(digitalTrialId)}
              fetchFullDigitalSurvey={fetchFullDigitalSurveyAction}
              fetchDigitalGroundTruth={fetchDigitalGroundTruthsAction}
            />
            {
              digitalSurvey && !digitalSurvey.exercise && (
                <DigitalTimerSetter digitalSurvey={digitalSurvey} />
              )
            }
          </Box>
          {
            digitalSurvey?.id && (
              <DigitalSectionToolbar
                submissionExist={submissionExist}
                digitalSections={digitalSections}
                selectedSection={selectedSection}
                setSelectedSection={setSelectedDigitalsectionAction}
                theme="tertiary"
                showMultipleSectionSelectionCheckBox
              />
            )
          }
        </Toolbar>
        {
          digitalSurvey?.id && (
            <>
              <Paper sx={{ p: 0 }} variant="outlined">
                <Typography variant="h4" sx={{ my: 2, ml: 2 }}>Cases</Typography>
                <Grid container sx={{ px: 2, pb: 2 }}>
                  <Grid item md={9} sm={12} xs={12}>
                    {selectedCase?.url && (
                      <Paper elevation={0}>
                        <Alert severity="info">
                          Please click on this&nbsp;
                          <MuiLink href="#" onClick={() => window.open(`${selectedCase?.url}?embeddedmode=true&skipacceptapplicationpolicy=true&disableswitchlabel=true`, '_blank')}>
                            link
                          </MuiLink>
                          &nbsp; if no digital image is displayed.
                        </Alert>
                      </Paper>
                    )}
                    <DLFrame frameSrc={selectedCase?.url} hideAnnotation={false} />
                  </Grid>
                  <Grid item md={3} sm={12} xs={12} className="relative">
                    <CaseList
                      caseList={digitalCases}
                      selectedCase={selectedCase}
                      digitalSectionId={selectedSection?.id}
                      digitalSurveyId={digitalSurvey.id}
                      setSelectedCase={setSelectedCaseAction}
                      setDigitalCases={setDigitalCasesAction}
                      addCase={addDigitalCaseAction}
                      deleteCase={deleteDigitalCaseAction}
                      updateCase={updateDigitalCaseAction}
                      showErrorNotification={showErrorNotificationAction}
                      submissionExist={submissionExist}
                      isEditorPrivileged={isEditorPrivileged}
                    />
                  </Grid>
                </Grid>
              </Paper>
              <Paper className="mt5" variant="outlined">
                <HeadingContainerDRT heading="Questions">
                  <QuestionEditor
                    surveyId={digitalSurvey.id}
                    trialId={digitalSurvey.trialId}
                    questions={digitalQuestions}
                    addQuestion={addDigitalQuestionAction}
                    updateQuestion={updateDigitalQuestionAction}
                    deleteQuestion={deleteDigitalQuestionAction}
                    onSortSave={saveQuestionSortOrder}
                    disabledQuestions={['ChoiceWithText']}
                    disableCopyQuestions
                    removeHideInAggregationCheckbox
                    showErrorNotification={showErrorNotificationAction}
                    submissionExist={submissionExist}
                  />
                </HeadingContainerDRT>
              </Paper>
              <Paper className="mt5" variant="outlined">
                <HeadingContainerDRT heading="Ground Truth">
                  <DigitalGroundTruthEditor
                    digitalQuestions={digitalQuestions}
                    digitalCases={digitalCases}
                    digitalSection={selectedSection}
                    digitalSurveyId={digitalSurvey.id}
                    DGTDictionaryByQuestionAndCase={DGTDictionaryByQuestionAndCase}
                    groundTruthGroupId={null}
                    addOrUpdateGroundTruth={addOrUpdateDigitalGroundTruthAction}
                    deleteGroundTruth={deleteDigitalGroundTruthAction}
                    showErrorNotification={showErrorNotificationAction}
                    submissionExist={submissionExist}
                  />
                </HeadingContainerDRT>
              </Paper>
              <Paper className="mt5" variant="outlined">
                <DigitalScoreEditor
                  selectedSection={selectedSection}
                  updateSection={updateDigitalSectionAction}
                  notificationAction={showNotificationAction}
                  showErrorNotification={showErrorNotificationAction}
                  submissionExist={submissionExist}
                />
              </Paper>
              {
                digitalSurvey && !digitalSurvey.exercise && (
                  <Paper className="mt5" variant="outlined">
                    <HeadingContainerDRT heading="Context Questions">
                      <DigitalContextQuestionEditor
                        surveyId={digitalSurvey.id}
                        digitalQuestions={nonEvaluableDigitalQuestions}
                        addDigitalQuestion={addDigitalQuestionAction}
                        updateDigitalQuestion={updateDigitalQuestionAction}
                        deleteDigitalQuestion={deleteDigitalQuestionAction}
                      />
                    </HeadingContainerDRT>
                  </Paper>
                )
              }
            </>
          )
        }
      </ContentBox>
    </ColorContext.Provider>
  );
};

const mapStateToProps = (state: RootState) => ({
  digitalSurvey: state.SurveyData.survey,
  digitalSections: selectDigitalSections(state),
  selectedSection: selectSelectedSection(state),
  sectionIds: state.SurveyData.digitalSections.allIds,
  selectedCase: selectSelectedCase(state),
  digitalCases: selectDigitalCases(state),
  digitalQuestions: selectNormalQuestionsDRT(state),
  nonEvaluableDigitalQuestions: selectContextQuestionsDRT(state),
  DGTDictionaryByQuestionAndCase: selectDigitalGTDictionaryByQuestionAndCaseDRT(
    state,
    state.SurveyData.digitalSections.selectedSection,
  ),
  isEditorPrivileged: selectIsEditorPrivileged(state),
});

const dispatchProps = {
  fetchFullDigitalSurvey,
  setSelectedDigitalsection,
  updateDigitalSection,
  setSelectedCase,
  addDigitalCase,
  setDigitalCases,
  updateDigitalCase,
  deleteDigitalCase,
  addDigitalQuestion,
  updateDigitalQuestion,
  deleteDigitalQuestion,
  setDigitalQuestions,
  fetchDigitalGroundTruths,
  addOrUpdateDigitalGroundTruth,
  deleteDigitalGroundTruth,
  showNotification,
  showErrorNotification,
};
const connector = connect(mapStateToProps, dispatchProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DigitalSurveyEditor);
